import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Code as CodeIcon,
  Speed as SpeedIcon,
  Devices as DevicesIcon,
  Api as ApiIcon,
  Storage as DatabaseIcon,
  Web as WebIcon,
  PhoneAndroid as MobileIcon,
  Cloud as CloudIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ApplicationDevelopmentCapability: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/dev-technology-contracts');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to Dev Technology Contracts"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Application Development Capability
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Full-Stack Development for Mission-Critical Government Systems
          </Typography>
        </Box>
      </Box>

      {/* Capability Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Capability Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Dev Technology Group delivers comprehensive application development services across the full technology 
            stack for mission-critical government systems. Our development teams leverage Agile methodologies, modern 
            frameworks, and industry best practices to build scalable, secure, and user-friendly applications that 
            support DHS and CBP operations. From trade processing systems handling $2.5T+ in annual commerce to student 
            tracking systems managing 1.5M+ international students, our applications power critical homeland security 
            missions.
          </Typography>
        </CardContent>
      </Card>

      {/* Development Methodologies */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Agile Development Methodologies
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                Scrum Framework
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">2-week sprint cycles</Typography></li>
                <li><Typography variant="body2">Daily stand-ups and sprint planning</Typography></li>
                <li><Typography variant="body2">Sprint reviews and retrospectives</Typography></li>
                <li><Typography variant="body2">Product backlog management</Typography></li>
                <li><Typography variant="body2">Cross-functional scrum teams</Typography></li>
                <li><Typography variant="body2">Velocity tracking and burndown charts</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                Kanban Methodology
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Continuous flow development</Typography></li>
                <li><Typography variant="body2">Visual workflow management</Typography></li>
                <li><Typography variant="body2">Work-in-progress (WIP) limits</Typography></li>
                <li><Typography variant="body2">Pull-based task assignment</Typography></li>
                <li><Typography variant="body2">Cycle time optimization</Typography></li>
                <li><Typography variant="body2">Just-in-time delivery</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
                SAFe (Scaled Agile)
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Enterprise-scale agile delivery</Typography></li>
                <li><Typography variant="body2">Program Increment (PI) planning</Typography></li>
                <li><Typography variant="body2">Agile Release Trains (ARTs)</Typography></li>
                <li><Typography variant="body2">Portfolio-level coordination</Typography></li>
                <li><Typography variant="body2">Lean-Agile principles</Typography></li>
                <li><Typography variant="body2">Continuous delivery pipeline</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Full-Stack Development */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Full-Stack Development Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WebIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Frontend Development
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Modern, responsive user interfaces for government applications.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>React:</strong> Component-based UI development</Typography></li>
                <li><Typography variant="body2"><strong>Angular:</strong> Enterprise application framework</Typography></li>
                <li><Typography variant="body2"><strong>Vue.js:</strong> Progressive JavaScript framework</Typography></li>
                <li><Typography variant="body2"><strong>TypeScript:</strong> Type-safe JavaScript development</Typography></li>
                <li><Typography variant="body2"><strong>Material-UI:</strong> Component library and design system</Typography></li>
                <li><Typography variant="body2"><strong>Redux/MobX:</strong> State management solutions</Typography></li>
                <li><Typography variant="body2"><strong>Responsive Design:</strong> Mobile-first, accessible interfaces</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Backend Development
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Scalable, secure server-side application logic.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Java/Spring Boot:</strong> Enterprise Java applications</Typography></li>
                <li><Typography variant="body2"><strong>.NET Core:</strong> Cross-platform .NET development</Typography></li>
                <li><Typography variant="body2"><strong>Node.js:</strong> JavaScript server-side runtime</Typography></li>
                <li><Typography variant="body2"><strong>Python/Django:</strong> Rapid application development</Typography></li>
                <li><Typography variant="body2"><strong>Microservices:</strong> Service-oriented architecture</Typography></li>
                <li><Typography variant="body2"><strong>RESTful APIs:</strong> HTTP-based web services</Typography></li>
                <li><Typography variant="body2"><strong>GraphQL:</strong> Flexible API query language</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DatabaseIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Database Development
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Robust data persistence and management solutions.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Oracle Database:</strong> Enterprise relational database</Typography></li>
                <li><Typography variant="body2"><strong>SQL Server:</strong> Microsoft database platform</Typography></li>
                <li><Typography variant="body2"><strong>PostgreSQL:</strong> Open-source relational database</Typography></li>
                <li><Typography variant="body2"><strong>MongoDB:</strong> NoSQL document database</Typography></li>
                <li><Typography variant="body2"><strong>Redis:</strong> In-memory data store and cache</Typography></li>
                <li><Typography variant="body2"><strong>Database Design:</strong> Schema design, normalization</Typography></li>
                <li><Typography variant="body2"><strong>Performance Tuning:</strong> Query optimization, indexing</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MobileIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Mobile Development
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Native and cross-platform mobile applications.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>iOS Development:</strong> Swift, Objective-C, Xcode</Typography></li>
                <li><Typography variant="body2"><strong>Android Development:</strong> Kotlin, Java, Android Studio</Typography></li>
                <li><Typography variant="body2"><strong>React Native:</strong> Cross-platform mobile apps</Typography></li>
                <li><Typography variant="body2"><strong>Flutter:</strong> Google's UI toolkit for mobile</Typography></li>
                <li><Typography variant="body2"><strong>Progressive Web Apps:</strong> Web-based mobile experiences</Typography></li>
                <li><Typography variant="body2"><strong>Mobile Security:</strong> Secure authentication, data encryption</Typography></li>
                <li><Typography variant="body2"><strong>Offline Capabilities:</strong> Local storage, sync</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* API Development */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <ApiIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        API Development & Integration
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                RESTful API Development
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">HTTP-based web services (GET, POST, PUT, DELETE)</Typography></li>
                <li><Typography variant="body2">JSON/XML data formats</Typography></li>
                <li><Typography variant="body2">API versioning and documentation (Swagger/OpenAPI)</Typography></li>
                <li><Typography variant="body2">Authentication (OAuth 2.0, JWT, API keys)</Typography></li>
                <li><Typography variant="body2">Rate limiting and throttling</Typography></li>
                <li><Typography variant="body2">CORS and security headers</Typography></li>
                <li><Typography variant="body2">Error handling and status codes</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                Microservices Architecture
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Service decomposition and domain-driven design</Typography></li>
                <li><Typography variant="body2">API Gateway patterns (Kong, AWS API Gateway)</Typography></li>
                <li><Typography variant="body2">Service discovery (Consul, Eureka)</Typography></li>
                <li><Typography variant="body2">Inter-service communication (REST, gRPC, message queues)</Typography></li>
                <li><Typography variant="body2">Circuit breakers and fault tolerance</Typography></li>
                <li><Typography variant="body2">Distributed tracing (Jaeger, Zipkin)</Typography></li>
                <li><Typography variant="body2">Container orchestration (Kubernetes)</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Technology Stack */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <DevicesIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Technology Stack & Tools
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Category</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Technologies</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Use Cases</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Frontend Frameworks</strong></TableCell>
              <TableCell>React, Angular, Vue.js, TypeScript</TableCell>
              <TableCell>User interfaces, dashboards, portals</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Backend Languages</strong></TableCell>
              <TableCell>Java, .NET, Node.js, Python</TableCell>
              <TableCell>Business logic, APIs, services</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Databases</strong></TableCell>
              <TableCell>Oracle, SQL Server, PostgreSQL, MongoDB</TableCell>
              <TableCell>Data persistence, analytics</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Cloud Platforms</strong></TableCell>
              <TableCell>AWS, Azure, Google Cloud</TableCell>
              <TableCell>Infrastructure, hosting, services</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>DevOps Tools</strong></TableCell>
              <TableCell>Jenkins, GitLab CI/CD, Docker, Kubernetes</TableCell>
              <TableCell>Automation, deployment, orchestration</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Testing Frameworks</strong></TableCell>
              <TableCell>JUnit, Jest, Selenium, Cypress</TableCell>
              <TableCell>Unit, integration, E2E testing</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Version Control</strong></TableCell>
              <TableCell>Git, GitHub, GitLab, Bitbucket</TableCell>
              <TableCell>Source code management, collaboration</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Custom Applications Portfolio */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Custom Applications Portfolio
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                Trade & Cargo Systems
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">ACE (Automated Commercial Environment)</Typography></li>
                <li><Typography variant="body2">ATAP (Advanced Trade Analytics Program)</Typography></li>
                <li><Typography variant="body2">Cargo processing and manifest systems</Typography></li>
                <li><Typography variant="body2">Trade compliance and enforcement</Typography></li>
                <li><Typography variant="body2">Import/export documentation</Typography></li>
                <li><Typography variant="body2">Duty calculation and payment</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                Identity & Student Systems
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">SEVIS (Student and Exchange Visitor Information System)</Typography></li>
                <li><Typography variant="body2">Biometric identity verification</Typography></li>
                <li><Typography variant="body2">Identity and access management (IAM)</Typography></li>
                <li><Typography variant="body2">School certification portals</Typography></li>
                <li><Typography variant="body2">Student compliance tracking</Typography></li>
                <li><Typography variant="body2">Multi-factor authentication</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
                Security & Collaboration
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">TSA security and screening systems</Typography></li>
                <li><Typography variant="body2">ICE collaboration platforms</Typography></li>
                <li><Typography variant="body2">Document management systems</Typography></li>
                <li><Typography variant="body2">Workflow automation tools</Typography></li>
                <li><Typography variant="body2">Communication and messaging</Typography></li>
                <li><Typography variant="body2">Secure file sharing</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Development Best Practices */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Development Best Practices & Standards
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Code Quality:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Code reviews and pair programming</Typography></li>
                <li><Typography variant="body2">Static code analysis (SonarQube, ESLint)</Typography></li>
                <li><Typography variant="body2">Coding standards and style guides</Typography></li>
                <li><Typography variant="body2">Technical debt management</Typography></li>
                <li><Typography variant="body2">Refactoring and optimization</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Testing & Quality Assurance:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Test-driven development (TDD)</Typography></li>
                <li><Typography variant="body2">Unit testing (80%+ code coverage)</Typography></li>
                <li><Typography variant="body2">Integration and E2E testing</Typography></li>
                <li><Typography variant="body2">Performance and load testing</Typography></li>
                <li><Typography variant="body2">Security testing and vulnerability scanning</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Documentation:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">API documentation (Swagger/OpenAPI)</Typography></li>
                <li><Typography variant="body2">Code comments and inline documentation</Typography></li>
                <li><Typography variant="body2">Architecture decision records (ADRs)</Typography></li>
                <li><Typography variant="body2">User guides and training materials</Typography></li>
                <li><Typography variant="body2">Runbooks and operational procedures</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Security & Compliance:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Secure coding practices (OWASP Top 10)</Typography></li>
                <li><Typography variant="body2">Authentication and authorization</Typography></li>
                <li><Typography variant="body2">Data encryption (at rest and in transit)</Typography></li>
                <li><Typography variant="body2">Section 508 accessibility compliance</Typography></li>
                <li><Typography variant="body2">FISMA and FedRAMP compliance</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Metrics & Performance */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Development Metrics & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                80%+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Code Coverage
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Unit test coverage
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                2 Week
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sprint Cycles
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Agile delivery cadence
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                99.9%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Uptime SLA
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Application availability
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Support
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Operations coverage
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ApplicationDevelopmentCapability;
